import { Dataset, DatasetId } from '../../meta/types/wms/Dataset';
import { Layer, isUsWestDem } from '../../meta/types/wms/Layer';
import { CONFIG } from '../../meta/types/config';

export const CODEDE_COLLECTIONS = new Set([
  DatasetId.S2L2A,
  DatasetId.S2L1C,
  DatasetId.S3OLCI,
  DatasetId.S3SLSTR,
  DatasetId.S5PL2,
  DatasetId.BYOC,
]);
export const CREODIAS_COLLECTIONS = new Set([
  DatasetId.S3OLCI,
  DatasetId.S3SLSTR,
  DatasetId.S5PL2,
  DatasetId.BYOC,
]);
export const AWS_EU_CENTRAL_1_COLLECTIONS = new Set([
  DatasetId.S1GRD,
  DatasetId.S2L1C,
  DatasetId.S2L2A,
  DatasetId.BYOC,
]);
export const AWS_US_WEST_2_COLLECTIONS = new Set([
  DatasetId.L8L1C,
  DatasetId.MODIS,
  DatasetId.DEM,
  DatasetId.BYOC,
]);
export const MUNDI_COLLECTIONS = new Set([
  DatasetId.S1GRD,
  DatasetId.S2L1C,
  DatasetId.S2L2A,
  DatasetId.S5PL2,
  DatasetId.BYOC,
]);

const filterBySet = (supportedCollections: Set<DatasetId>, collections: Dataset[]): Dataset[] => {
  return collections.filter(dataset => supportedCollections.has(dataset.id));
};

export const filterCodeDEDatasets = (collections: Dataset[]): Dataset[] =>
  filterBySet(CODEDE_COLLECTIONS, collections);

export const filterCreoDatasets = (collections: Dataset[]): Dataset[] =>
  filterBySet(
    new Set([
      ...Array.from(CREODIAS_COLLECTIONS),
      ...Array.from(AWS_EU_CENTRAL_1_COLLECTIONS),
      ...Array.from(AWS_US_WEST_2_COLLECTIONS),
    ]),
    collections,
  );

export const filterSgsDatasets = (collections: Dataset[]): Dataset[] =>
  filterBySet(
    new Set([
      ...Array.from(CREODIAS_COLLECTIONS),
      ...Array.from(AWS_EU_CENTRAL_1_COLLECTIONS),
      ...Array.from(AWS_US_WEST_2_COLLECTIONS),
      ...Array.from(MUNDI_COLLECTIONS),
    ]),
    collections,
  );

const getEndpointUrl = (layer: Layer) => {
  const layerDataset = layer.datasourceDefaults.type;
  // DEM is handled depending on the demInstance selected (MAPZEN = us-west, COP = eu-central)
  if (layerDataset === DatasetId.DEM) {
    const { demInstance } = layer.datasourceDefaults;
    if (isUsWestDem(demInstance)) {
      return CONFIG.usWestWmsServiceBasePath;
    }
    return CONFIG.wmsConfigurationServiceBasePath;
  }
  if (AWS_EU_CENTRAL_1_COLLECTIONS.has(layerDataset)) {
    return CONFIG.wmsConfigurationServiceBasePath;
  }
  if (AWS_US_WEST_2_COLLECTIONS.has(layerDataset)) {
    return CONFIG.usWestWmsServiceBasePath;
  }
  if (CREODIAS_COLLECTIONS.has(layerDataset)) {
    return CONFIG.byocCreodiasServiceBasePath;
  }
};

const getExtAuthEndpointUrl = (
  layer: Layer,
  currentAuthCollections: Set<DatasetId> | undefined,
  defaultEndpoint: string,
): string | undefined => {
  const { type } = layer.datasourceDefaults;
  if (currentAuthCollections !== undefined && currentAuthCollections.has(type)) {
    return defaultEndpoint;
  }
  return getEndpointUrl(layer);
};

const doLayersUseSameSource = (
  layers: Layer[],
  currentAuthCollections: Set<DatasetId> | undefined,
  defaultEndpoint: string,
): boolean => {
  const layerUrl = getExtAuthEndpointUrl(layers[0], currentAuthCollections, defaultEndpoint);
  return layers.every(
    layer => getExtAuthEndpointUrl(layer, currentAuthCollections, defaultEndpoint) === layerUrl,
  );
};

export const getServiceEndpoint = (
  layers: Layer[] | undefined,
  currentAuthCollections: Set<DatasetId> | undefined,
  defaultEndpoint: string,
) => {
  if (layers === undefined || layers.length === 0) {
    return defaultEndpoint;
  }
  if (doLayersUseSameSource(layers, currentAuthCollections, defaultEndpoint)) {
    return getExtAuthEndpointUrl(layers[0], currentAuthCollections, defaultEndpoint);
  }
};
